/**
 * How to render a portable section, given nothing but the shared `refreshKey`/`onChange`
 * contract every self-contained card converged on across the modular-layout work (D102,
 * D104-D110). This is what lets any page render any card a reader moves onto it — a page
 * component no longer needs to know how to build `<TasksCard>`; it just needs to know the
 * id showed up in `sectionsForPage(thisPage)`.
 *
 * `add`, `notice` and `first-run` used to be missing here, pinned to `Briefing.tsx` because
 * each needed something only that page could wire up — `first-run` in particular needed
 * `TasksCard`'s own `onEmptyStateChange`, which meant nothing anywhere else. D118 made all
 * three genuinely self-contained (`QuickAddCard`, `NoticeCard`, `FirstRunCard` each fetch or
 * subscribe to what they need on their own), so they are one-line lookups like everything
 * else here now, and `renderSection` no longer needs an override escape hatch for them.
 */

import type { ReactNode } from "react";
import { AreasCard } from "../components/AreasCard";
import { ProjectsCard } from "../components/ProjectsCard";
import { ActivityCard } from "../components/ActivityCard";
import { QuickAddCard } from "../components/QuickAddCard";
import { NoticeCard } from "../components/NoticeCard";
import { FirstRunCard } from "../components/FirstRunCard";
import { WinsCard } from "../components/WinsCard";
import { GoneQuietCard } from "../components/GoneQuietCard";
import { InboxCard } from "../components/InboxCard";
import { TasksCard } from "../components/TasksCard";
import { StarredCard } from "../components/StarredCard";
import { RecurringCard } from "../components/RecurringCard";
import { NotesCard } from "../components/NotesCard";
import { CaptureTierCard } from "../components/CaptureTierCard";
import { DisplayCard } from "../components/DisplayCard";
import { AccountCard } from "../components/AccountCard";
import { SessionsCard } from "../components/SessionsCard";
import { InvitesCard } from "../components/InvitesCard";
import { TokensCard } from "../components/TokensCard";
import { ModelConfigsCard } from "../components/ModelConfigsCard";
import { ModelSlotsCard } from "../components/ModelSlotsCard";
import { PromptsCard } from "../components/PromptsCard";
import { ContextCard } from "../components/ContextCard";
import { BlackjackPage } from "../pages/BlackjackPage";
import type { SectionId } from "./model";

export type CardContext = {
  refreshKey: number;
  onChange: () => void | Promise<void>;
};

export const SECTION_REGISTRY: Partial<Record<SectionId, (ctx: CardContext) => ReactNode>> = {
  add: ({ refreshKey, onChange }) => (
    <QuickAddCard refreshKey={refreshKey} onChange={onChange} />
  ),
  activity: ({ refreshKey, onChange }) => (
    <ActivityCard refreshKey={refreshKey} onChange={onChange} />
  ),
  notice: () => <NoticeCard />,
  "first-run": ({ refreshKey }) => <FirstRunCard refreshKey={refreshKey} />,
  wins: ({ refreshKey }) => <WinsCard refreshKey={refreshKey} />,
  "gone-quiet": ({ refreshKey, onChange }) => (
    <GoneQuietCard refreshKey={refreshKey} onChange={onChange} />
  ),
  starred: ({ refreshKey, onChange }) => (
    <StarredCard refreshKey={refreshKey} onChange={onChange} />
  ),
  recurring: ({ refreshKey, onChange }) => (
    <RecurringCard refreshKey={refreshKey} onChange={onChange} />
  ),
  inbox: ({ refreshKey, onChange }) => (
    <InboxCard refreshKey={refreshKey} onChange={onChange} />
  ),
  tasks: ({ refreshKey, onChange }) => (
    <TasksCard refreshKey={refreshKey} onChange={onChange} />
  ),
  notes: ({ refreshKey, onChange }) => (
    <NotesCard refreshKey={refreshKey} onChange={onChange} />
  ),
  blackjack: () => <BlackjackPage />,
  areas: ({ refreshKey, onChange }) => (
    <AreasCard refreshKey={refreshKey} onChange={onChange} />
  ),
  projects: ({ refreshKey, onChange }) => (
    <ProjectsCard refreshKey={refreshKey} onChange={onChange} />
  ),
  "settings-display": () => <DisplayCard />,
  "settings-capture": () => <CaptureTierCard />,
  "settings-account": () => <AccountCard />,
  "settings-sessions": () => <SessionsCard />,
  "settings-invites": () => <InvitesCard />,
  "settings-tokens": ({ refreshKey }) => <TokensCard refreshKey={refreshKey} />,
  "models-configs": ({ refreshKey, onChange }) => (
    <ModelConfigsCard refreshKey={refreshKey} onChange={onChange} />
  ),
  "models-slots": ({ refreshKey, onChange }) => (
    <ModelSlotsCard refreshKey={refreshKey} onChange={onChange} />
  ),
  "models-prompts": ({ refreshKey, onChange }) => (
    <PromptsCard refreshKey={refreshKey} onChange={onChange} />
  ),
  "models-context": ({ refreshKey, onChange }) => (
    <ContextCard refreshKey={refreshKey} onChange={onChange} />
  ),
};

export function renderSection(id: SectionId, ctx: CardContext): ReactNode {
  return SECTION_REGISTRY[id]?.(ctx) ?? null;
}
